import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import postImage from '../../assets/brand-icons/round_island_rental.png';

export const FeatureCommentReply = () => {
  const { t } = useTranslation('landing');

  const comments = [
    { key: 'comment1', hasReply: true },
    { key: 'comment2', hasReply: false },
    { key: 'comment3', hasReply: false },
    { key: 'comment4', hasReply: false },
  ];

  return (
    <Box component="section" sx={{ py: { xs: '60px', md: '100px' }, background: '#f8fafc', overflow: 'hidden' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: '20px', md: '40px' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '62% 35%' }, gap: '3%', alignItems: 'center' }}>
          {/* Mockup (left on desktop) */}
          <Box className="omni-feature-visual omni-reveal" sx={{ order: { xs: 2, md: 1 } }}>
            <Box className="omni-mockup-frame">
              <Box className="omni-mockup-chrome">
                <Box className="omni-mockup-dots"><span /><span /><span /></Box>
                <Box className="omni-mockup-title">{t('featureComment.mockup.title')}</Box>
              </Box>
              <Box className="mock-comment">
                {/* Post Side */}
                <Box className="mock-post-side" sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Box className="mock-post-header">
                    <Box className="mock-post-avatar" sx={{ background: '#1877F2' }}>
                      {t('featureComment.mockup.pageName').charAt(0)}
                    </Box>
                    <Box className="mock-post-meta">
                      <Box className="page-name">{t('featureComment.mockup.pageName')}</Box>
                      <Box className="post-time">{t('featureComment.mockup.postDate')}</Box>
                    </Box>
                  </Box>
                  <Box className="mock-post-text">{t('featureComment.mockup.postText')}</Box>
                  <Box className="mock-post-image" sx={{
                    background: `url(${postImage}) center/cover no-repeat`,
                    position: 'relative',
                    height: 200,
                  }}>
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)', borderRadius: '8px' }} />
                    <Box sx={{ position: 'absolute', bottom: 6, left: 8, color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                      {t('featureComment.mockup.postImageLabel')}
                    </Box>
                  </Box>
                  <Box className="mock-post-stats">
                    <span>❤️ {t('featureComment.mockup.statsLikes')}</span>
                    <span>💬 {t('featureComment.mockup.statsComments')}</span>
                    <span>↗️ {t('featureComment.mockup.statsShares')}</span>
                  </Box>
                </Box>

                {/* Comments Side */}
                <Box className="mock-comments-side">
                  <Box className="mock-comments-header">
                    <span className="mock-comments-title">{t('featureComment.mockup.commentsTitle')}</span>
                    <Box className="mock-comment-stats">
                      <span className="mock-comment-stat" style={{ background: '#dcfce7', color: '#166534' }}>{t('featureComment.mockup.aiRepliedCount')}</span>
                      <span className="mock-comment-stat" style={{ background: '#fff7ed', color: '#c2410c' }}>{t('featureComment.mockup.pendingCount')}</span>
                    </Box>
                  </Box>
                  <Box className="mock-comment-list">
                    {comments.map((c) => {
                      const statusText = t(`featureComment.mockup.${c.key}.status`);
                      const isPending = statusText.includes('⏳') || statusText.includes('待');
                      return (
                        <Box key={c.key}>
                          <Box className="mock-comment-item">
                            <Box className="mock-comment-ava">{t(`featureComment.mockup.${c.key}.avatar`)}</Box>
                            <Box className="mock-comment-body">
                              <Box className="mock-comment-bubble">
                                <span className="cname">{t(`featureComment.mockup.${c.key}.name`)}</span>{' '}
                                <span className="ctext">{t(`featureComment.mockup.${c.key}.text`)}</span>
                              </Box>
                              <span className="mock-comment-status" style={{
                                background: isPending ? '#fff7ed' : '#dcfce7',
                                color: isPending ? '#c2410c' : '#166534'
                              }}>
                                {statusText}
                              </span>
                            </Box>
                          </Box>
                          {c.hasReply && (
                            <Box className="mock-reply-indent">
                              <Box className="mock-reply-bubble">
                                <span className="mock-reply-ai-tag">AI</span>
                                <span>{t('featureComment.mockup.reply1')}</span>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Text (right on desktop) */}
          <Box className="omni-reveal omni-reveal-delay-1" sx={{ order: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#1877F2', mb: 2 }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              {t('featureComment.label')}
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: 'clamp(1.6rem, 3vw, 2.2rem)' }, fontWeight: 800, lineHeight: 1.3, mb: 2, letterSpacing: '-0.3px', color: '#0f1729' }}>
              {t('featureComment.title')}
            </Typography>
            <Typography sx={{ fontSize: 19, color: '#374151', lineHeight: 1.8, mb: '28px' }}>
              {t('featureComment.desc')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {['bullet1', 'bullet2', 'bullet3'].map((key) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '2px', fontSize: 15, fontWeight: 700, color: '#fff' }}>&#10003;</Box>
                  <Typography sx={{ fontSize: 18, color: '#374151', lineHeight: 1.5 }}>{t(`featureComment.${key}`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
